import { reaction } from 'mobx'
import { useState, useEffect, useMemo, useRef } from 'react'

import { FormField } from '@/Core/UI/Form'
import { FormContextProvider, createFormContext } from '@/Core/UI/Form/Providers'
import { BaseFieldTemplate } from '@/Ship/Templates/Form/BaseFieldTemplate'

export interface MultiAssignmentFieldCellProps {
    fieldName: string
    fieldDef: Record<string, any>
    initialValue?: any
    /**
     * Reports the chosen value:
     *   - select → IRI string (from value['@id'] or the string itself);
     *   - scalar (number/switch/date) → the raw value.
     */
    onChange: (value: any) => void
    /**
     * Reports the raw (un-normalised) value straight from the field — for a select
     * this is the full resource object ({ id, '@id', title, ... }), not just its IRI.
     * Optional: only needed by callers that must read e.g. `.id`/`.title` off a
     * relation (typicalRoute filtering by loadingPoint/unloadingPoint numeric id).
     */
    onRawChange?: (value: any) => void
    label?: string
    disabled?: boolean
}

/**
 * Standalone, resource-aware Core form field used inside the multi-create drawer.
 * Each cell owns an isolated form context (like AssignmentFormFieldCell) but
 * reports the RAW value for any field type — selects give their IRI, scalars
 * give their scalar — so the drawer can build a batch payload uniformly.
 */
export const MultiAssignmentFieldCell: React.FC<MultiAssignmentFieldCellProps> = ({
    fieldName,
    fieldDef,
    initialValue,
    onChange,
    onRawChange,
    label,
    disabled,
}) => {
    const saved = useRef<any>(initialValue ?? null)

    const [formStore] = useState(() =>
        createFormContext({
            fields: { [fieldName]: { ...fieldDef, label, isEditable: true } },
            initialValues: { [fieldName]: initialValue ?? null },
            isEditable: true,
        })
    )

    // Колбеки тримаємо у ref і оновлюємо на кожен рендер.
    //
    // Батько (MultiAssignmentDrawer) передає їх інлайновими стрілками, тож їхня
    // ідентичність змінюється на кожен рендер. Якби вони лишались у залежностях
    // ефекту нижче, reaction перестворювався б після КОЖНОГО введеного символа —
    // а разом з ним перемонтовувався інпут, і фокус злітав з поля. Через ref
    // reaction створюється один раз, але завжди кличе найсвіжіший колбек.
    const onChangeRef = useRef(onChange)
    const onRawChangeRef = useRef(onRawChange)
    onChangeRef.current = onChange
    onRawChangeRef.current = onRawChange

    useEffect(() => {
        const dispose = reaction(
            () => formStore.getValue(fieldName),
            (val: any) => {
                onRawChangeRef.current?.(val ?? null)
                // For select values (objects / IRIs) normalise to the IRI string;
                // scalars pass through untouched.
                const next = val && typeof val === 'object' ? (val['@id'] ?? null) : (val ?? null)
                if (next !== saved.current) {
                    saved.current = next
                    onChangeRef.current(next)
                }
            }
        )
        return () => dispose()
    }, [formStore, fieldName])

    // Re-applies fieldDef.inputProps whenever the parent passes a new fieldDef
    // object (e.g. typicalRoute's filter recomputed from loadingPoint/unloadingPoint) —
    // the cell's own formStore is created once, so live filter/dropdownRender changes
    // must be merged in reactively instead of being picked up only at mount.
    //
    // Залежність — СЕРІАЛІЗОВАНИЙ знімок, а не сам об'єкт: батько збирає inputProps
    // інлайново, тож посилання нове на кожен рендер, і порівняння за ідентичністю
    // писало б в observable inputProps після кожного символа (зайвий перерендер
    // інпута). Функції (dropdownRender/responseLabel) у JSON не серіалізуються —
    // їх ідентичність ігноруємо свідомо: вони не залежать від введеного значення.
    const inputPropsKey = JSON.stringify(fieldDef?.['inputProps'] ?? {})
    const inputPropsRef = useRef(fieldDef?.['inputProps'])
    inputPropsRef.current = fieldDef?.['inputProps']

    useEffect(() => {
        const field = formStore.getField(fieldName)
        field?.mergeInputProps({ ...(inputPropsRef.current ?? {}) })
    }, [formStore, fieldName, inputPropsKey])

    // Syncs an externally-reset initialValue (e.g. the parent clearing typicalRoute
    // when loadingPoint/unloadingPoint change) into the cell's own form value.
    // Guarded to only fire on a real external change, not on the user's own edit
    // (which already updated `saved.current` via the reaction above).
    useEffect(() => {
        const next = initialValue ?? null
        if (next !== saved.current) {
            saved.current = next
            formStore.setValue(fieldName, next)
        }
    }, [formStore, fieldName, initialValue])

    // Піддерево кешуємо і перебудовуємо лише коли реально змінюється щось із того,
    // що на нього впливає (поле, блокування).
    //
    // Це не мікрооптимізація, а частина фікса: ядровий FormField оголошує свій
    // внутрішній компонент прямо в тілі, тож на КОЖЕН свій рендер він віддає React
    // новий тип компонента — і React перемонтовує інпут разом із DOM-вузлом. Поки
    // комірка перерендерювалась на кожен символ (drawer -> setShared -> observer),
    // фокус злітав. Значення живе у власному formStore комірки, тому заморожене
    // піддерево лишається коректним: інпут читає його сам через MobX.
    const content = useMemo(
        () => (
            <div style={disabled ? { pointerEvents: 'none', opacity: 0.5 } : undefined}>
                <FormContextProvider value={formStore}>
                    <FormField name={fieldName} template={BaseFieldTemplate} ignoreParentField={true} />
                </FormContextProvider>
            </div>
        ),
        [formStore, fieldName, disabled]
    )

    return content
}
